import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as categoryApi from '../../../shared/api/categories.js'
import type { Category, CatalogStatus } from '../../../shared/types/models.js'
import { isArchived, type LifecycleAction } from '../../../shared/utils/catalog.js'
import CategoryForm from './CategoryForm.js'
import EmptyState from './EmptyState.js'
import StatusDot from '../../../shared/ui/StatusDot.js'
import IconButton from '../../../shared/ui/IconButton.js'
import { ArchiveIcon, GearIcon, PauseCircleIcon, PlayCircleIcon } from '../../../shared/ui/icons.js'

type LoadStatus = 'loading' | 'ready' | 'error'

interface Props {
  organizationId: string
  menuId: string
  menuStatus: CatalogStatus
  canManage: boolean
}

export default function CategoriesSection({ organizationId, menuId, menuStatus, canManage }: Props) {
  const { t } = useTranslation()

  const [categories, setCategories] = useState<Category[]>([])
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const menuArchived = isArchived(menuStatus)
  const mutationsDisabled = !canManage || menuArchived

  async function load() {
    setStatus('loading')
    setError('')
    try {
      const res = await categoryApi.getCategories(organizationId, menuId)
      setCategories(res.data)
      setStatus('ready')
    } catch {
      setError(t('menu.categoriesLoadError'))
      setStatus('error')
    }
  }

  useEffect(() => {
    void load()
  }, [organizationId, menuId])

  async function handleLifecycle(categoryId: string, action: LifecycleAction) {
    setPendingId(categoryId)
    setError('')
    try {
      const updated =
        action === 'suspend'
          ? await categoryApi.suspendCategory(organizationId, menuId, categoryId)
          : action === 'activate'
            ? await categoryApi.activateCategory(organizationId, menuId, categoryId)
            : await categoryApi.archiveCategory(organizationId, menuId, categoryId)
      setCategories(prev => prev.map(c => (c.id === categoryId ? updated : c)))
    } catch {
      setError(t('menu.lifecycleError'))
    } finally {
      setPendingId(null)
    }
  }

  function handleSaved(category: Category) {
    setCategories(prev => {
      const exists = prev.some(c => c.id === category.id)
      return exists ? prev.map(c => (c.id === category.id ? category : c)) : [...prev, category]
    })
    setShowCreate(false)
    setEditingId(null)
  }

  return (
    <div className="categories-section">
      <div className="page-header">
        <div>
          <h3>{t('menu.categories')}</h3>
          {menuArchived && <p className="page-section-description">{t('menu.archivedMenuNote')}</p>}
        </div>
        {canManage && !menuArchived && !showCreate && (
          <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
            {t('menu.addCategory')}
          </button>
        )}
      </div>

      {status === 'loading' && (
        <div className="page-loading">
          <div className="spinner" />
        </div>
      )}

      {status === 'error' && (
        <div className="settings-card">
          <div className="form-error-banner">{error}</div>
          <button type="button" className="btn btn-primary" onClick={() => void load()}>
            {t('error.retry')}
          </button>
        </div>
      )}

      {status === 'ready' && error && <div className="form-error-banner">{error}</div>}

      {status === 'ready' && (
        <>
          {canManage && !menuArchived && showCreate && (
            <CategoryForm
              organizationId={organizationId}
              menuId={menuId}
              disabled={false}
              onSaved={handleSaved}
              onCancel={() => setShowCreate(false)}
            />
          )}

          {categories.length === 0 ? (
            <EmptyState
              title={t('menu.categoriesEmpty')}
              hint={t('menu.categoriesEmptyHint')}
              actionLabel={canManage && !menuArchived ? t('menu.addCategory') : undefined}
              onAction={canManage && !menuArchived ? () => setShowCreate(true) : undefined}
            />
          ) : (
            <ul className="category-list">
              {categories.map(category => {
                const rowPending = pendingId === category.id
                const statusLabel = t(
                  category.status === 'ACTIVE'
                    ? 'menu.statusActive'
                    : category.status === 'SUSPENDED'
                      ? 'menu.statusSuspended'
                      : 'menu.statusArchived',
                )
                return (
                  <li key={category.id} className={`category-row category-row--${category.status.toLowerCase()}`} aria-busy={rowPending}>
                    <div className="category-compact-row">
                      <StatusDot status={category.status} label={statusLabel} />
                      <span className="category-name" title={category.name}>{category.name}</span>
                      <span className="category-slug" title={category.slug ?? ''}>{category.slug ?? '—'}</span>

                      {editingId !== category.id && canManage && !menuArchived && category.status !== 'ARCHIVED' && (
                        <div className="category-actions">
                          <IconButton
                            label={t('menu.edit')}
                            disabled={rowPending}
                            onClick={() => setEditingId(category.id)}
                          >
                            <GearIcon />
                          </IconButton>
                          <IconButton
                            label={category.status === 'ACTIVE' ? t('menu.suspend') : t('menu.activate')}
                            disabled={rowPending}
                            onClick={() => void handleLifecycle(category.id, category.status === 'ACTIVE' ? 'suspend' : 'activate')}
                          >
                            {rowPending ? (
                              <span className="icon-spinner" aria-label={t('menu.processing')} />
                            ) : category.status === 'ACTIVE' ? (
                              <PauseCircleIcon />
                            ) : (
                              <PlayCircleIcon />
                            )}
                          </IconButton>
                          <IconButton
                            label={t('menu.archive')}
                            disabled={rowPending}
                            danger
                            onClick={() => {
                              const ok = window.confirm(t('menu.archiveCategoryConfirm', { name: category.name }))
                              if (ok) void handleLifecycle(category.id, 'archive')
                            }}
                          >
                            <ArchiveIcon />
                          </IconButton>
                        </div>
                      )}
                    </div>

                    {editingId === category.id && (
                      <div className="category-edit">
                        <CategoryForm
                          organizationId={organizationId}
                          menuId={menuId}
                          initial={category}
                          disabled={mutationsDisabled}
                          onSaved={handleSaved}
                          onCancel={() => setEditingId(null)}
                        />
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </div>
  )
}